<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="w-full flex space-x-4 justify-end">
            <AppButton element="InertiaLink" :href="meta.links.practice_index">
                {{ $t('messages.practice') }}
            </AppButton>
        </div>

        <form action="" method="GET" @submit.prevent="submit">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div class="sm:col-span-2">
                    <label for="subject_id" class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $tc('messages.subject', 1) }}
                    </label>
                    <select
                        id="subject_id"
                        name="subject_id"
                        v-model="form.subject_id"
                        class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                        <option :value="null">{{ $t('messages.all') }}</option>
                        <option v-for="subject in subjects" :value="subject.id" :key="subject.id">
                            {{ subject.name }}
                        </option>
                    </select>
                </div>

                <div class="sm:col-span-2">
                    <div class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.trimester') }}
                    </div>
                    <div class="mt-1">
                        <div class="inline-flex items-center" v-for="trimester in [1, 2, 3]" :key="trimester">
                            <input
                                :id="`trimester_${trimester}`"
                                name="trimester"
                                type="radio"
                                :value="trimester"
                                v-model="form.trimester"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                            />
                            <label :for="`trimester_${trimester}`" class="ml-1 mr-3">
                                <span class="block text-sm leading-5 font-medium text-gray-700">
                                    {{ trimester }}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <EmptyState v-if="activities.data.length === 0" picture />

        <div class="md:hidden flex flex-col mt-5 divide-y divide-gray-200">
            <div v-for="(activity, index) in activities.data" :key="activity.id">
                <div class="p-2 my-2" :class="index % 2 === 0 ? 'bg-gray-50' : 'bg-white'">
                    <span class="truncate">{{ activity.title }}</span>
                    <div class="flex flex-wrap items-center justify-between">
                        <div class="text-gray-600">
                            {{ activity.subject }}
                        </div>
                        <div>
                            <GradePill :grade-pill="activity.gradePill" />
                        </div>
                        <div>
                            <InertiaSquareLinkButton
                                action="quiz"
                                :href="activity.links.take_fixed_quiz"
                                v-if="activity.links.take_fixed_quiz"
                            />
                            <InertiaSquareLinkButton
                                action="show"
                                :href="activity.links.show_fixed_quiz"
                                v-if="activity.links.show_fixed_quiz"
                            />
                            <InertiaSquareLinkButton
                                action="show"
                                :href="activity.links.show"
                                v-if="activity.links.show"
                            />
                            <div v-if="activity.has_feedback" :title="$t('messages.feedback')">
                                <svg
                                    class="w-6 h-6 text-indigo-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                            </div>
                            <div v-if="activity.materials_for_students_exists" :title="$tc('messages.materials', 2)">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="w-6 h-6 text-indigo-500"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    stroke-width="2"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                    <path
                                        d="M15 7l-6.5 6.5a1.5 1.5 0 0 0 3 3l6.5 -6.5a3 3 0 0 0 -6 -6l-6.5 6.5a4.5 4.5 0 0 0 9 9l6.5 -6.5"
                                    ></path>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="hidden md:flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                ></th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.title') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.subject', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    v-if="false"
                                >
                                    {{ $tc('messages.dimensions', 2) }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.grades', 1) }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(activity, index) in activities.data"
                                :key="activity.id"
                            >
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex space-x-1 items-center"
                                >
                                    <InertiaSquareLinkButton
                                        action="quiz"
                                        :href="activity.links.take_fixed_quiz"
                                        v-if="activity.links.take_fixed_quiz"
                                    />
                                    <InertiaSquareLinkButton
                                        action="show"
                                        :href="activity.links.show_fixed_quiz"
                                        v-if="activity.links.show_fixed_quiz"
                                    />
                                    <InertiaSquareLinkButton
                                        action="show"
                                        :href="activity.links.show"
                                        v-if="activity.links.show"
                                    />
                                    <div v-if="activity.has_feedback" :title="$t('messages.feedback')">
                                        <svg
                                            class="w-6 h-6 text-indigo-500"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                                                clip-rule="evenodd"
                                            ></path>
                                        </svg>
                                    </div>
                                    <div
                                        v-if="activity.materials_for_students_exists"
                                        :title="$tc('messages.materials', 2)"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="w-6 h-6 text-indigo-500"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            stroke-width="2"
                                            stroke="currentColor"
                                            fill="none"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        >
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                            <path
                                                d="M15 7l-6.5 6.5a1.5 1.5 0 0 0 3 3l6.5 -6.5a3 3 0 0 0 -6 -6l-6.5 6.5a4.5 4.5 0 0 0 9 9l6.5 -6.5"
                                            ></path>
                                        </svg>
                                    </div>
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    {{ activity.title }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ activity.subject }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500" v-if="false">
                                    <template v-if="activity.dimensionCategories">
                                        <div v-for="(category, index) in activity.dimensionCategories" :key="index">
                                            {{ category.name }}:
                                            <span
                                                v-for="(dimension, dimensionIndex) in category.dimensions"
                                                :title="dimension.title"
                                                :key="dimensionIndex"
                                            >
                                                {{ dimension.abbreviation }}
                                            </span>
                                        </div>
                                    </template>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <GradePill :grade-pill="activity.gradePill" />
                                </td>
                                <!--                            <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">-->
                                <!--                                @foreach($activity->activityDimensionsByCategory() as $categoryName => $category)-->
                                <!--                                @unless($loop->first)-->
                                <!--                                <br>-->
                                <!--                                @endunless-->
                                <!--                                {{ $categoryName }}:-->
                                <!--                                @foreach($category as $activityDimension)-->
                                <!--                                <span title="{{ $activityDimension->dimension->title }}">-->
                                <!--                                                D{{ $activityDimension->dimension->number }}-->
                                <!--                                            </span>-->
                                <!--                                @endforeach-->
                                <!--                                @endforeach-->
                                <!--                            </td>-->
                                <!--                            <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">-->
                                <!--                                &lt;!&ndash;suppress JSAnnotator, JSUnusedLocalSymbols, JSUnresolvedVariable &ndash;&gt;-->
                                <!--                                <x-grade-pill :activity-response="$activity->activityResponse"></x-grade-pill>-->
                                <!--                            </td>-->
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="activities" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import GradePill from '@/Pages/Student/Activity/GradePill.vue';
import AppButton from '@/components/AppButton.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        AppButton,
        GradePill,
        PanelPage,
        InertiaPagination,
        InertiaSquareLinkButton,
        EmptyState,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'subjects', 'activities', 'filters'],
    data() {
        return {
            form: this.$inertia.form('Student/Activity/Index', {
                subject_id: this.filters.subject_id,
                trimester: this.filters.trimester,
            }),
        };
    },
    methods: {
        submit() {
            this.form.get(route('students.activities.index'));
        },
    },
};
</script>
